import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Modal,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../../configs"; // sesuaikan path
import { getUser } from "../../../utils/DataUser";

import { canCreate, canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;


const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const apiBase = process.env.REACT_APP_PDF_URL || "/api";
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [qrUrl, setQrUrl] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrRecord, setQrRecord] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const openQR = (record) => {
    const url = `${apiBase}/incoming/${record.id_incoming}/qrcode`;
    setQrUrl(url);
    setQrRecord(record);
    setShowQrModal(true);
  };

  const getStrippingLabel = (val) => {
    if (val === 1) return 'Sudah';
    if (val === 2) return 'Sudah (Sebagian)';
    if (val === 3) return 'Sudah (Perlu Revisi)';
    return 'Belum';
  };

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const user = getUser();

      // Build params - untuk user level 7, tambahkan filter id_customer
      const params = {
        page,
        limit: pageSize,
        customer_name: searchName || undefined,
        document_number: searchCode || undefined,
      };

      // Filter di backend untuk user level 7 (Importir)
      if (user && (user.id_user_level === 7 || user.id_user_level === "7")) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/incoming", { params });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      // Baca total dari pagination response
      const total = raw.pagination?.total ||
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pemasukan barang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/incoming/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_incoming);
    try {
      await axiosInstance.delete(`/incoming/${record.id_incoming}`);
      message.success("Pemasukan barang berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pemasukan barang");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";



  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO BPB",
      dataIndex: "bpb_number",
      width: 130,
    },
    {
      title: "TANGGAL BPB",
      dataIndex: "bpb_date",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "KODE DOKUMEN",
      dataIndex: "id_document",
      width: 130,
      render: (value) => value || "-",
    },
    {
      title: "NO DOKUMEN",
      dataIndex: "document_number",
      width: 160,
      render: (value) => value || "-",
    },
    {
      title: "TANGGAL DOKUMEN",
      dataIndex: "document_date",
      width: 150,
      render: (value) => formatDate(value),
    },
    {
      title: "PEMILIK BARANG",
      dataIndex: "nama_customer",
      width: 160,
      render: (value, record) =>
        value || record.id_contact ? value || record.id_contact : "-",
    },
    {
      title: "STATUS",
      dataIndex: "stripping",
      width: 180,
      render: (value) => {
        let color = "default";
        let label = "-";

        if (value === 1) {
          color = "green";
          label = "SELESAI PEMBONGKARAN";
        } else if (value === 0) {
          color = "orange";
          label = "BELUM PEMBONGKARAN";
        } else if (value === 2) {
          color = "gold"; // kuning
          label = "PEMBONGKARAN SEBAGIAN";
        } else if (value === 3) {
          color = "red";
          label = "SEGERA REVISI DOKUMEN";
        }
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: 6,
              fontWeight: 600,
              color: "#fff",
              background:
                color === "green"
                  ? "#1BC5BD"
                  : color === "orange"
                    ? "#F64E60"
                    : color === "gold"
                      ? "#FFA800"
                      : color === "red"
                        ? "#F64E60"
                        : "#888888",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? "Lihat Detil" : "Detil & Edit Pemasukan Barang"}>
              <Link to={`updateincoming/${record.id_incoming}`}>
                <Button
                  size="small"
                  type="primary"
                  icon={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? <SearchOutlined /> : <EditOutlined />}
                  style={{
                    background: "#3699FF",
                    borderColor: "#3699FF",
                  }}
                />
              </Link>
            </Tooltip>
          )}

          <Tooltip title="Lihat & Cetak QR Code">
            <Button
              size="small"
              type="primary"
              icon={<QrcodeOutlined />}
              style={{
                background: "#3699FF",
                borderColor: "#3699FF",
              }}
              onClick={() => openQR(record)}
            />
          </Tooltip>

          {canDelete(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
            <Popconfirm
              title="Hapus Pemasukan Barang"
              description={`Yakin ingin menghapus "${record.bpb_number || record.nama_customer
                }"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_incoming,
              }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_incoming}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={12} style={{ marginBottom: { xs: 16, md: 0 } }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pemasukan Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pemasukan Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Space wrap>
            {canCreate(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
              <Link to="/inventory/addIncoming">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Item..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari kode..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
        <Modal
          title="QR CODE PEMASUKAN BARANG"
          open={showQrModal}
          onCancel={() => { setShowQrModal(false); setQrRecord(null); }}
          width={380}
          footer={null}
          bodyStyle={{ padding: 16 }}
        >
          {qrUrl && (
            <div style={{ textAlign: 'center' }}>
              <img
                src={qrUrl}
                alt="QR Code Pemasukan"
                style={{ width: 280, height: 280, display: 'block', margin: '0 auto' }}
              />
              {qrRecord && (
                <div style={{ marginTop: 14, textAlign: 'left', fontSize: 12, background: '#f8f9fa', borderRadius: 6, padding: '10px 14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 4 }}>
                    <span style={{ fontWeight: 600 }}>No BPB</span>
                    <span>: {qrRecord.bpb_number || '-'}</span>
                    <span style={{ fontWeight: 600 }}>Tgl BPB</span>
                    <span>: {formatDate(qrRecord.bpb_date)}</span>
                    <span style={{ fontWeight: 600 }}>No Dokumen</span>
                    <span>: {qrRecord.document_number || '-'}</span>
                    <span style={{ fontWeight: 600 }}>Importir</span>
                    <span>: {qrRecord.nama_customer || qrRecord.id_contact || '-'}</span>
                    <span style={{ fontWeight: 600 }}>Pembongkaran</span>
                    <span>: {getStrippingLabel(qrRecord.stripping)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </Row>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_item"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
